<template>
    <div>
        <div
            v-if="answerAt !== null"
            class="userHealthSurveyQuestionsWrapper answered d-flex align-items-center justify-content-center">
            <div class="answerAtWrapper">
                <div>回答を受け付けました</div>
                <div>回答日時：{{ helper.dateTimeForHuman(answerAt, user.timezone) }}</div>
                <button
                    class="backBtn btn"
                    :disabled="inProgress"
                    @click="handleClickBackBtn">戻る
                </button>
            </div>
        </div>
        <div
            v-else-if="healthSurveyQuestions !== null"
            class="userHealthSurveyQuestionsWrapper">
            <div class="healthSurveyQuestionHeading d-flex align-items-center justify-content-center">
                <div class="healthSurveyQuestionHeadingInner">
                    <div><span>質問に答えて</span><span></span></div>
                    <div><span>心身の健康をチェックしましょう</span><span></span></div>
                </div>
            </div>

            <div
                v-for="( q, idx ) in healthSurveyQuestions"
                class="healthSurveyQuestionItem"
                :class="{ 'lastItem' : healthSurveyQuestions.length - 1 === idx }">
                <label :class="{ 'is-required-ja' : q.flg_required === 1 }">{{ q.title }}</label>

                <div class="mb-3">
                    <template v-if="Number(q.interview_question_type_id) === questionTypes.input">
                        <input
                            v-model="q.content"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid' : detectError('health_survey_questions', idx) || q.content && q.content.length > 255 }"
                            :id="`q-${q.id}`">
                    </template>

                    <template v-else-if="Number(q.interview_question_type_id) === questionTypes.multiInput">
                        <textarea
                            v-model="q.content"
                            class="form-control"
                            :class="{ 'is-invalid' : detectError('health_survey_questions', idx) || q.content && q.content.length > 500 }"
                            :id="`q-${q.id}`">
                        </textarea>
                    </template>

                    <template v-else-if="Number(q.interview_question_type_id) === questionTypes.radio">
                        <div v-for="item in q.items" class="form-check">
                            <input
                                v-model="q.content"
                                :value="item.title"
                                type="radio"
                                class="form-check-input"
                                :id="`q-${q.id}-item-${item.id}`">
                            <label
                                class="form-check-label"
                                :for="`q-${q.id}-item-${item.id}`">
                                {{ item.title }}
                            </label>
                        </div>

                        <div v-if="Number(q.flg_other) === 1" class="form-check">
                            <input
                                v-model="q.content"
                                value="その他"
                                type="radio"
                                class="form-check-input"
                                :id="`q-${q.id}-item-other`">
                            <label
                                class="form-check-label"
                                :for="`q-${q.id}-item-other`">
                                その他
                            </label>
                        </div>
                    </template>

                    <template v-else-if="Number(q.interview_question_type_id) === questionTypes.check">
                        <div v-for="item in q.items" class="form-check">
                            <input
                                v-model="q.content"
                                :value="item.title"
                                type="checkbox"
                                class="form-check-input"
                                :id="`q-${q.id}-item-${item.id}`">
                            <label
                                class="form-check-label"
                                :for="`q-${q.id}-item-${item.id}`">
                                {{ item.title }}
                            </label>
                        </div>

                        <div v-if="Number(q.flg_other) === 1" class="form-check">
                            <input
                                v-model="q.content"
                                value="その他"
                                type="checkbox"
                                class="form-check-input"
                                :id="`q-${q.id}-item-other`">
                            <label
                                class="form-check-label"
                                :for="`q-${q.id}-item-other`">
                                その他
                            </label>
                        </div>
                    </template>

                    <div
                        v-if="!hasLengthValidateMessage('health_survey_questions', idx)"
                        class="invalid-feedback d-block mt-1">
                        {{ validateMessage('health_survey_questions', idx) }}
                    </div>

                    <template v-if="Number(q.interview_question_type_id) === questionTypes.input">
                        <div
                            class="d-flex justify-content-between text-muted small mt-1"
                            :class="{ 'is-invalid-color' : q.content && q.content.length > 255 }">
                            <div>255文字以内で回答してください</div>
                            <div>{{ q.content !== null ? q.content.length : 0 }}/255</div>
                        </div>
                    </template>
                    <template v-else-if="Number(q.interview_question_type_id) === questionTypes.multiInput">
                        <div
                            class="d-flex justify-content-between text-muted small mt-1"
                            :class="{ 'is-invalid-color' : q.content && q.content.length > 500 }">
                            <div>500文字以内で回答してください</div>
                            <div>{{ q.content !== null ? q.content.length : 0 }}/500</div>
                        </div>
                    </template>
                </div>
            </div>

            <div class="d-flex justify-content-center">
                <button
                    class="surveyBtn btn btn-info"
                    :disabled="inProgress"
                    @click="handleClickAnswerBtn">回答を完了する
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import constants from '../../constants'
import YokumiruHelper from '../../YokumiruHelper'

export default {
    props: {
        user: {
            type: Object,
            required: true,
        },
        options: {
            type: Object|null,
            required: true,
        },
        groupMetadata: {
            type: Object,
            required: true,
        },
    },

    data: function () {
        return {
            inProgress: false,
            helper: YokumiruHelper,
            questionTypes: constants.interview_question_types,
            healthSurveyQuestions: null,
            answerAt: null,
            errors: {},
        }
    },

    computed: {},

    created: function () {
        this.fetchHealthSurveyQuestions()
    },

    methods: {
        /**
         * ヘルスアンサー質問取得
         */
        fetchHealthSurveyQuestions: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.user.groups.endpoint.base
            await axios.get(`${endpoint}/${this.groupMetadata.group_id}/health_survey/questions`, this.options)
                .then(({data}) => {
                    this.healthSurveyQuestions = data.data
                })

            this.inProgress = false
        },

        /**
         * 質問を未取得の状態にする
         */
        clearQuestions: function () {
            this.healthSurveyQuestions = null
        },

        /**
         * エラーメッセージ判定
         */
        detectError: function (attribute, idx) {
            return Object.keys(this.errors).some(key => key === `${attribute}.${idx}`)
        },

        /**
         * エラーメッセージ表示
         */
        validateMessage: function (attribute, idx) {
            const filtered = Object.keys(this.errors)
                .filter(key => key === `${attribute}.${idx}`)
                .map(key => this.errors[key][0])

            return filtered.length > 0 ? filtered[0] : null
        },

        /**
         * 文字数制限のエラーメッセージ判定
         */
        hasLengthValidateMessage: function (attribute, idx) {
            const result = this.validateMessage(attribute, idx)
            if (!result) return false

            return result.includes('255') || result.includes('500')
        },

        /**
         * 「回答する」ボタン押下時
         * @param {object} e
         */
        handleClickAnswerBtn: async function(e) {
            e.preventDefault()

            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.user.groups.endpoint.base
            const params = { health_survey_questions: this.healthSurveyQuestions }

            await axios.post(`${endpoint}/${this.groupMetadata.group_id}/health_surveys`, params, this.options)
                .then(({data}) => {
                    this.errors = {}
                    this.answerAt = data.answer_at
                    this.clearQuestions()
                    bus.$emit('health-survey-answered')
                })
                .catch(({response}) => {
                    if (response.hasOwnProperty('data') && response.data.hasOwnProperty('errors')) {
                        bus.$emit('alert-danger')
                        this.errors = response.data.errors
                    }
                    else {
                        bus.$emit('alert-danger', 'システムエラーが発生しました。')
                    }
                })

            this.inProgress = false
        },

        /**
         * 戻るボタン押下時
         * @param {object} e
         */
        handleClickBackBtn: function (e) {
            e.preventDefault()
            bus.$emit('health-survey-back')
        },
    },
}
</script>

<style lang="scss" scoped>
.userHealthSurveyQuestionsWrapper {
    max-width: 500px;
    margin: 0 auto;

    &.answered {
        height: calc(100vh - 100px);

        @media screen and (max-width:767px) {
            height: calc(100vh - 220px);
        }
    }

    .healthSurveyQuestionHeading {
        margin-bottom: 25px;
        font-size: 18px;
        font-weight: bold;
        text-align: center;
        color: #504F4F;

        .healthSurveyQuestionHeadingInner {
            > div {
                position: relative;

                span:nth-child(1) {
                    position: relative;
                    z-index: 2;
                }

                span:nth-child(2) {
                    position: absolute;
                    z-index: 1;
                    left: 50%;
                    bottom: 2px;
                    transform: translate3d(-50%, 0, 0);
                    display: block;
                    width: 114px;
                    height: 8px;
                    background: #FFFB00;
                    opacity: 0.7;
                }

                &:nth-child(2) {
                    span:nth-child(2) {
                        width: 274px;
                    }
                }
            }
        }
    }

    .healthSurveyQuestionItem {
        padding: 20px 0;
        border-top: 3px solid #56CED6;

        &.lastItem {
            border-bottom: 3px solid #56CED6;
        }
    }

    .surveyBtn {
        width: 80%;
        margin: 40px auto 200px;
        border-radius: 42px;
    }

    .answerAtWrapper {
        text-align: center;

        > div {
            &:nth-child(1) {
                color: #504F4F;
                font-size: 18px;
                font-weight: bold;
            }
            &:nth-child(2) {
                color: #9E9E9E;
                font-size: 13px;
            }
        }

        .backBtn {
            width: 80%;
            margin: 20px auto 0;
            color: #fff;
            border-radius: 42px;
            background: #9E9E9E;
        }
    }
}
</style>
